import * as THREE from 'three'
import { types, Instance, getParent, getSnapshot } from 'mobx-state-tree'
import { CoverPlate } from 'src/api/models/Enclosure/Cover'
import { Printedcircuitboard } from 'src/api/models/Content/Printedcircuitboard'
import {
  Typename,
  coverPlate,
  elementEdge,
  printedcircuitboard
} from 'src/constants/typenames'
import { fromGlobalId } from 'src/api/utils/globalId'
import { IPrintedcircuitboard } from 'src/api/models/Content/Printedcircuitboard/types'
import { ICoverPlate } from 'src/api/models/Enclosure/Cover/types'
import { IProject } from 'src/api/models/Project/types'
import {
  IElementEdge,
  IElementEdgeReference,
  IReference,
  IReferenceComponent
} from './types'
import { IElementConnection } from '../types'
import { ElementEdge } from '.'

type ElementEdgeReferenceIdentifierType = Extract<
  Typename,
  typeof printedcircuitboard | typeof coverPlate | typeof elementEdge
>

const ReferenceComponent = (): IReferenceComponent =>
  types.reference(
    types.union(
      types.late(Printedcircuitboard) as IPrintedcircuitboard,
      types.late(CoverPlate) as ICoverPlate,
      types.late(() => ElementEdge()) as IElementEdge
    ),
    {
      get(
        identifier: string,
        parent: Instance<ReturnType<typeof ElementEdge>>
      ) {
        const { type } = fromGlobalId(identifier) as {
          type: ElementEdgeReferenceIdentifierType
        }
        let reference = null
        switch (type) {
          case elementEdge:
            const elementConnection = getParent(parent, 3)
            reference = (elementConnection as any).edges.find(
              (edge: any) => edge.id === identifier
            )
            break
          case coverPlate:
            // 4. CoverPlate 3. ElementConnection 2. types.array 1. ElementEdge
            const plate = getParent(parent, 4)
            reference = plate
            break
          case printedcircuitboard:
            // @ts-ignore 11. Project 10. EnclosureConnection 9. types.array  8. CoverEdge  7. Cover 6. types.array 5. CoverPlateEdge 4. CoverPlate  3. ElementConnection 2. types.array 1. ElementEdge
            const project = getParent<Instance<IProject>>(parent, 12)
            const pcb = project.content.edges[0].node
            reference = pcb
            break
        }
        return reference !== undefined ? reference : null
      },
      set(instance) {
        return instance.id
      }
    }
  )

export const ElementEdgeReference: IElementEdgeReference = types
  .model(`${elementEdge}Reference`, {
    value: types.maybe(types.late(ReferenceComponent))
  })
  .views((self) => {
    // 3.ElementConnection 2. types.array 1. ElementEdge
    const parentconnection = getParent<Instance<IElementConnection>>(self, 3)
    const elementEdge = getParent<Instance<IElementEdge>>(self)
    return {
      get options() {
        // @ts-ignore
        return parentconnection.getReferenceOptions(elementEdge.id)
      }
    }
  })
  .actions((self) => {
    return {
      afterAttach() {
        if (self.value === undefined) {
          // 4. CoverPlate 3.ElementConnection 2. types.array 1. ElementEdge
          const coverPlate = getParent<Instance<ICoverPlate>>(self, 4)
          self.value = coverPlate.id as any
        }
      },
      update(reference: string | { id: string }) {
        const value =
          typeof reference === 'string'
            ? (reference as string)
            : (reference.id as string)

        const elementEdge = getParent<Instance<IElementEdge>>(self)
        const snapshot = getSnapshot(elementEdge)
        console.log(snapshot)
        const [
          cx,
          cy
        ] = elementEdge.properties.getParentReferencePointCoordinates()

        /**
         *
         */
        const referenceID: string =
          typeof reference === 'string' ? reference : reference.id

        const referenceInstance = self.options.find(
          ({ id }) => id === referenceID
        ) as Instance<IReference>

        const [
          nx,
          ny
        ] = elementEdge.properties.getParentReferencePointCoordinates(
          undefined,
          referenceInstance
        )
        const [ox, oy] = [nx - cx, ny - cy]

        const {
          x: { value: x },
          y: { value: y }
        } = snapshot.properties.offset
        const op = 'replace'
        const change = [
          { op, path: '/reference/value', value },
          {
            op,
            path: '/properties/offset',
            value: { x: { value: x - ox }, y: { value: y - oy } }
          }
        ]
        const reverseChange = [
          {
            op,
            path: '/reference/value',
            value: typeof self.value === 'string' ? self.value : self.value?.id
          },
          {
            op,
            path: '/properties/offset',
            value: snapshot.properties.offset
          }
        ]

        elementEdge.properties.offset.update({ x: x - ox, y: y - oy }, false)

        elementEdge.patch([change, reverseChange])

        self.value?.blur?.()
        self.value =
          typeof reference === 'string'
            ? (reference as any)
            : (reference.id as any)
        !!(elementEdge.focused || elementEdge.node.focused) &&
          self.value?.focusReferencePoint?.({
            referencePoint: elementEdge.properties.parentReferencePoint.value,
            side: elementEdge.side
          })
      }
    }
  })
